import Dexie, { EntityTable } from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { EventData, Joyride, STATUS, Status, Step } from 'react-joyride';
import OnboardingTooltip from 'src/components/organisms/OnboardingTooltip/OnboardingTooltip';
import { Module } from 'src/domain';

const styleTimelineTour: Step[] = [
    {
        target: '.title-card',
        content: 'Welcome to Styletimeline! Let me guide you through some of the features.',
        skipBeacon: true,
    },
    {
        target: '.table-toolbar',
        content:
            'This toolbar provides quick access to key actions and controls. We’ll look at each in detail, but here’s your command center!',
    },
    {
        target: '.ag-side-buttons',
        content:
            'Here you can apply flexible filters to narrow down your data and add or remove columns to customize your view.',
    },
    { target: '.storefront-type-select', content: 'Select the storefront type to tailor the data view accordingly.' },
    {
        target: '.filter-and-column-settings',
        content:
            ' Save and load custom views by managing filter and column presets here. Use the dropdown to select, save, or delete presets.',
    },
    {
        target: '.fullscreen-button',
        content: 'Enter fullscreen mode for an immersive view.',
    },
    { target: '.enable-advanced-filters', content: 'Enable advanced filtering for more complex data queries.' },
    { target: '.edit-all-partners', content: 'Edit data for all partners in one go.' },
    {
        target: '.discard-button',
        content:
            'Discard your changes to revert to the last saved state. Always review your updates before leaving this page! ',
    },
    {
        target: '.save-button',
        content:
            'Save your work to keep changes. Don’t forget that your changes persist until the next day. Always review your updates before leaving this page! ',
    },
];

const salesCampaignsTour: Step[] = [
    {
        target: '.title-card',
        content: 'Welcome to Sales Campaigns! Let me guide you through some of the key features.',
        skipBeacon: true,
    },
    {
        target: '.add-campaign-button',
        content: 'Create a new sales campaign here. Click to get started with your next sale!',
    },
    {
        target: '.manage-campaign-items',
        content:
            'Click this button to access the campaign items table where you can edit and select products, assign discounts, and set eligibility for sales.',
    },
    {
        target: '.show-sales-summary-report',
        content:
            'Explore detailed sales reports across tabs: overall summary, category splits, store sales, and stock age.',
    },
    {
        target: '.download-store-campaign-briefing',
        content:
            'Download store briefings with sale styles, discounts, and stock levels for all stores or selected stores.',
    },
    {
        target: '[data-testid^="monitorDownloadButton-"]',
        content:
            'Download the latest weekly performance report for all stores or selected stores. This report is available for active campaigns and up to six months after a campaign ends.',
    },
    {
        target: '.edit-campaign-details',
        content:
            'Edit campaign details like name and dates. Note: storefront type is fixed and cannot be changed here.',
    },
    {
        target: '.delete-campaign',
        content: 'Delete campaign and all associated data permanently. Use with caution.',
    },
];

interface TourSeen {
    key: string;
    read: boolean;
}

export interface ToursDatabase extends Dexie {
    tourSeen: EntityTable<TourSeen, 'key'>;
}

function createToursDatabase() {
    const db = new Dexie('TourDatabase') as ToursDatabase;
    db.version(1).stores({
        tourSeen: 'key',
    });
    return db;
}

const db = createToursDatabase();

interface TourContextValue {
    startTour: () => void;
    run: boolean;
    stopTour: () => void;
    steps: Step[];
}

const TOURS: Record<string, Step[]> = {
    'style-timeline': [...styleTimelineTour],
    'sales-campaigns': [...salesCampaignsTour],
};

const TourContext = createContext<TourContextValue | undefined>(undefined);

export const useTourContext = (): TourContextValue => {
    const context = useContext(TourContext);
    if (!context) throw new Error('useTourContext must be used within a TourProvider');
    return context;
};

interface TourProviderProps {
    currentModule: Module | undefined;
    children: ReactNode;
}

export const TourProvider: React.FC<TourProviderProps> = ({ currentModule, children }) => {
    const seenData = useLiveQuery(() => {
        if (!currentModule) return [];
        return db.tourSeen.where('key').equals(currentModule).toArray();
    }, [currentModule]);

    const [run, setRun] = useState(false);
    const [steps, setSteps] = useState<Step[]>([]);

    useEffect(() => {
        if (seenData === undefined) return;

        setSteps(currentModule ? TOURS[currentModule] || [] : []);
        const seen = seenData.length > 0 && seenData[0].read === true;

        if (seen) {
            setRun(false);
        } else {
            setRun(true);
        }
    }, [currentModule, seenData]);

    const startTour = useCallback(() => {
        if (steps.length > 0) {
            setRun(true);
        }
    }, [steps]);

    const stopTour = useCallback(() => {
        setRun(false);
        db.tourSeen.put({ key: currentModule, read: true });
    }, [currentModule]);

    const handleJoyrideCallback = useCallback(
        (data: EventData) => {
            const statuses = [STATUS.FINISHED, STATUS.SKIPPED] as Status[];
            if (statuses.includes(data.status)) {
                stopTour();
            }
        },
        [stopTour]
    );

    const values = useMemo(() => {
        return {
            startTour,
            stopTour,
            run,
            steps,
        };
    }, [startTour, run, stopTour, steps]);

    return (
        <TourContext.Provider value={values}>
            {children}
            <Joyride
                steps={steps}
                run={run}
                continuous
                onEvent={handleJoyrideCallback}
                options={{
                    showProgress: true,
                    scrollOffset: 100,
                    width: 400,
                    zIndex: 2000,
                }}
                styles={{
                    tooltipContainer: {
                        width: 400,
                    },
                }}
                tooltipComponent={OnboardingTooltip}
            />
        </TourContext.Provider>
    );
};
